// Web Push helpers for Canal Boats Telemark.
// Handles: permission request, VAPID key fetch, PushManager.subscribe(),
// backend sync, app-badge updates, in-app "ding" sound on push arrival.

import { api } from "./api";

const SOUND_URL = "/notification.wav";
let audioEl = null;

function getAudio() {
    if (audioEl) return audioEl;
    try {
        audioEl = new Audio(SOUND_URL);
        audioEl.preload = "auto";
        audioEl.volume = 0.55;
    } catch {
        audioEl = null;
    }
    return audioEl;
}

export function playNotificationSound() {
    const a = getAudio();
    if (!a) return;
    try {
        a.currentTime = 0;
        const p = a.play();
        if (p && p.catch) p.catch(() => { /* autoplay blocked – ignore */ });
    } catch { /* noop */ }
}

export function setBadge(count) {
    if (typeof count !== "number" || count < 0) return;
    try {
        if ("setAppBadge" in navigator) {
            navigator.setAppBadge(count).catch(() => {});
        }
    } catch { /* noop */ }
}

export function clearBadge() {
    try {
        if ("clearAppBadge" in navigator) {
            navigator.clearAppBadge().catch(() => {});
        }
    } catch { /* noop */ }
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

export function pushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function permissionState() {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission; // "default" | "granted" | "denied"
}

async function getRegistration() {
    // sw.js is registered from index.html – wait for it to be ready.
    return navigator.serviceWorker.ready;
}

export async function currentSubscription() {
    if (!pushSupported()) return null;
    try {
        const reg = await getRegistration();
        return await reg.pushManager.getSubscription();
    } catch {
        return null;
    }
}

export async function enablePush() {
    if (!pushSupported()) {
        throw new Error("Denne enheten støtter ikke push-varsler.");
    }
    // Ask permission FIRST – must happen inside a user gesture.
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
        throw new Error(
            perm === "denied"
                ? "Du har blokkert varsler for denne siden. Skru dem på i nettleser-innstillingene og prøv igjen."
                : "Varsler ble ikke aktivert."
        );
    }
    const reg = await getRegistration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const { data } = await api.get("/push/vapid-public-key");
        const applicationServerKey = urlBase64ToUint8Array(data.public_key);
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
        });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
    });
    // Unlock audio for the coming in-app dings by playing once (allowed
    // inside the user-gesture click that triggered this call).
    playNotificationSound();
    return true;
}

export async function disablePush() {
    if (!pushSupported()) return;
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        try {
            await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        } catch { /* ignore – we still unsubscribe locally */ }
        try { await sub.unsubscribe(); } catch { /* noop */ }
    }
    clearBadge();
}

export async function sendTestPush() {
    return api.post("/push/test");
}

// Wire up the global service-worker → app message channel exactly once.
// Called from AuthProvider on mount.
let listenerInstalled = false;
export function installPushListener() {
    if (listenerInstalled) return;
    if (!("serviceWorker" in navigator)) return;
    listenerInstalled = true;
    navigator.serviceWorker.addEventListener("message", (event) => {
        const data = event.data || {};
        if (data.type === "PUSH_RECEIVED") {
            if (typeof data.unread_count === "number") setBadge(data.unread_count);
            playNotificationSound();
        }
        if (data.type === "PUSH_SUBSCRIPTION_CHANGED") {
            // Refresh subscription silently.
            enablePush().catch(() => { /* user will re-enable manually */ });
        }
    });
}

// PWA install prompt handling (Chrome/Edge/Android). iOS is handled via
// instructions instead – Safari doesn't fire beforeinstallprompt.
let deferredInstallPrompt = null;
if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredInstallPrompt = e;
    });
}

export function canInstallPwa() {
    return !!deferredInstallPrompt;
}

export async function promptInstallPwa() {
    if (!deferredInstallPrompt) return false;
    deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    return choice.outcome === "accepted";
}

export function isIos() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}
